import { useEffect, useState } from "react";

const DEFAULT_LEVEL_COUNT = 10;

function silentLevels(levelCount: number): number[] {
  return Array.from({ length: levelCount }, () => 0);
}

export function useAudioLevels({
  audioContext,
  audioSource,
  isActive,
  levelCount = DEFAULT_LEVEL_COUNT,
}: {
  audioContext?: AudioContext | null;
  audioSource?: MediaStreamAudioSourceNode | null;
  isActive: boolean;
  levelCount?: number;
}): number[] {
  const [levels, setLevels] = useState<number[]>(() =>
    silentLevels(levelCount),
  );

  useEffect(() => {
    if (!isActive || !audioContext || !audioSource) {
      setLevels(silentLevels(levelCount));
      return;
    }

    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    const samples = new Uint8Array(analyser.frequencyBinCount);
    let animationFrameId: number | null = null;

    try {
      audioSource.connect(analyser);
    } catch {
      setLevels(silentLevels(levelCount));
      return;
    }

    const updateLevels = () => {
      analyser.getByteTimeDomainData(samples);
      const peak = samples.reduce(
        (max, value) => Math.max(max, Math.abs(value - 128) / 128),
        0,
      );

      setLevels((previousLevels) => [...previousLevels.slice(1), peak]);
      animationFrameId = window.requestAnimationFrame(updateLevels);
    };

    updateLevels();

    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }

      try {
        audioSource.disconnect(analyser);
        analyser.disconnect();
      } catch {
        return;
      }
    };
  }, [audioContext, audioSource, isActive, levelCount]);

  return levels;
}
